import printIcon from "@assets/images/print-inquiry.svg";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import MyTypography from "@saleor/components/MyTypography";
import StickyHorizontalScrollBar from "@saleor/components/StickyHorizontalScrollBar";
import TableCellHeader from "@saleor/components/TableCellHeader";
import { BIG_ROW_NUMBER_SET } from "@saleor/components/TablePagination/constants";
import TablePaginationCustom from "@saleor/components/TablePaginationCustom";
import { CustomerOrdersFromSapQuery } from "@saleor/graphql";
import { ExporOrderListSortField } from "@saleor/inquirySearchOrder/urls";
import { makeStyles } from "@saleor/macaw-ui";
import { renderCollection } from "@saleor/misc";
import { ListProps, SortPage } from "@saleor/types";
import { getArrowDirection } from "@saleor/utils/sort";
import classNames from "classnames";
import React, { useRef } from "react";
import SVG from "react-inlinesvg";
import { useIntl } from "react-intl";

enum EStatusType {
  "Received Order" = "#A3AED0",
  "Credit/Cash Issue" = "#FFC107",
  "Partial Committed Order" = "#3860BE",
  "Ready for delivery" = "#007BFF",
  "Partial Delivery" = "#009FB8",
  "Completed Delivery" = "#6CC04A",
  "Cancelled" = "#E41B17",
  "Completed Order" = "#1E7D34"
}

interface IOrderListTable extends ListProps, SortPage<ExporOrderListSortField> {
  data: CustomerOrdersFromSapQuery["customerOrdersFromSap"];
  settings: any;
  onUpdateListSettings: any;
  loading?: boolean;
  totalCount: number | null;
  latestPageItemNumber: number | null;
  onPreviousFirstPage: () => void;
  onNextLastPage: () => void;
  onDownloadPdfOrder: (orderId, soNo) => void;
}

const useStyles = makeStyles(
  theme => ({
    root: {
      overflowX: "auto",
      width: "100%",
      border: `1px solid ${theme.palette.grey[300]}`,
      borderRadius: "2px",
      "& .MuiTableCell-head": {
        "& *": {
          background: "#DAF7FB",
          color: `${theme.palette.grey[900]} !important`
        }
      },
      "& .MuiTableRow-head": {
        "& *": {
          background: "#DAF7FB"
        }
      },
      "&::-webkit-scrollbar": {
        height: "14px",
        background: "#FFFFFF"
      },
      "&::-webkit-scrollbar-track": {
        borderRadius: 10
      },
      "&::-webkit-scrollbar-thumb": {
        background: "#CCCCCC",
        borderRadius: 10,
        border: "4px solid #FFFFFF",
        height: "6px"
      },
      "& .MuiTableCell-head * ": {
        color: "#222222 !important"
      }
    },
    cursorPointer: {
      cursor: "pointer"
    },
    link: {
      cursor: "pointer"
    },
    colPiNumber: {
      paddingLeft: 20,
      background: "inherit",
      borderRight: `2px solid ${theme.palette.grey[300]}`,
      position: "sticky",
      left: 96,
      zIndex: 2,
      whiteSpace: "nowrap",
      "& .MuiTypography-root": {
        color: theme.palette.info.main
      }
    },
    col96: {
      minWidth: 96,
      width: 96
    },
    col100: {
      minWidth: 100,
      width: 100
    },
    col120: {
      minWidth: 120,
      whiteSpace: "nowrap",
      color: theme.palette.grey[900]
    },
    col150: {
      minWidth: 150,
      color: theme.palette.grey[900],
      whiteSpace: "nowrap"
    },
    col180: {
      minWidth: 180,
      color: theme.palette.grey[900],
      whiteSpace: "nowrap"
    },
    pagination: {
      background: "rgba(212, 244, 248, 0.8)"
    },
    paginateFooter: {
      padding: "12px 0px"
    },
    rowClickable: {
      cursor: "pointer"
    },
    colFreeze: {
      position: "sticky",
      right: 0,
      borderLeft: `1px solid ${theme.palette.grey[300]}`,
      minWidth: 48,
      padding: 0,
      boxShadow: "-4px 0px 8px rgb(0 0 0 / 4%)",
      borderRadius: "0px 2px 2px 0px",
      background: "inherit",
      textAlign: "center"
    },
    colViewDuplicate: {
      position: "sticky",
      left: 0,
      display: "table-cell",
      justifyItems: "center",
      paddingRight: "0px !important",
      paddingLeft: "12px !important",
      zIndex: 1,
      borderRadius: "0px 2px 2px 0px",
      background: "inherit",
      textAlign: "center"
    },
    statusIcon: {
      width: 8,
      height: 8,
      marginRight: 8,
      borderRadius: "50%"
    },
    title: {
      marginTop: 40,
      marginBottom: 24
    },
    flex: { display: "flex", alignItems: "center" },
    flexStart: {
      display: "flex",
      alignItems: "center",
      justifyContent: "start"
    },
    firstChild: {
      paddingLeft: 32
    },
    printBtn: {
      padding: "4px 15px",
      borderRadius: "4px",
      background: "#FFFFFF"
    }
  }),
  { name: "OrderListTable1" }
);

const OrderListTable: React.FC<IOrderListTable> = props => {
  const {
    data,
    disabled,
    settings,
    totalCount,
    pageInfo,
    latestPageItemNumber,
    onUpdateListSettings,
    sort,
    onSort,
    onPreviousPage,
    onNextPage,
    onNextLastPage,
    onPreviousFirstPage,
    onDownloadPdfOrder
  } = props;
  const intl = useIntl();
  const classes = useStyles();
  const containerRef = useRef<HTMLDivElement | null>(null);

  const renderStatus = (item: string) => (
    <div className={classes.flex}>
      <div
        className={classes.statusIcon}
        style={{ backgroundColor: EStatusType[item] }}
      ></div>
      <MyTypography variant="body3">{item}</MyTypography>
    </div>
  );

  const handleDownloadPdf = item => {
    onDownloadPdfOrder(item.id, item.sdDoc);
  };

  return (
    <>
      <MyTypography className={classes.title} variant="h7">
        {`รายการที่เกี่ยวข้อง ${totalCount}`}
      </MyTypography>

      <div className={classes.root} ref={containerRef}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCellHeader className={classes.colViewDuplicate}>
                <Box
                  display={"flex"}
                  alignItems="center"
                  justifyContent={"center"}
                >
                  <SVG src={printIcon} />
                  <Box mr={1} />
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({ defaultMessage: "Print" })}
                  </MyTypography>
                </Box>
              </TableCellHeader>
              <TableCellHeader
                className={classes.colPiNumber}
                direction={
                  sort.sort === ExporOrderListSortField.soNo
                    ? getArrowDirection(sort.asc)
                    : undefined
                }
                arrowPosition="right"
                onClick={() => onSort(ExporOrderListSortField.soNo)}
              >
                <MyTypography variant="subtitle3">
                  {intl.formatMessage({ defaultMessage: "S/O No." })}
                </MyTypography>
              </TableCellHeader>
              <TableCellHeader
                className={classNames(classes.col120, classes.firstChild)}
                direction={
                  sort.sort === ExporOrderListSortField.poNo
                    ? getArrowDirection(sort.asc)
                    : undefined
                }
                arrowPosition="right"
                onClick={() => onSort(ExporOrderListSortField.poNo)}
              >
                <MyTypography variant="subtitle3">
                  <Box pl={1}>
                    {intl.formatMessage({ defaultMessage: "PO No." })}
                  </Box>
                </MyTypography>
              </TableCellHeader>
              <TableCellHeader
                className={classes.col120}
                direction={
                  sort.sort === ExporOrderListSortField.contractNo
                    ? getArrowDirection(sort.asc)
                    : undefined
                }
                arrowPosition="right"
                onClick={() => onSort(ExporOrderListSortField.contractNo)}
              >
                <MyTypography variant="subtitle3">
                  {intl.formatMessage({ defaultMessage: "Contract No." })}
                </MyTypography>
              </TableCellHeader>
              <TableCellHeader className={classes.col180}>
                <MyTypography variant="subtitle3">
                  {intl.formatMessage({ defaultMessage: "Sold-to Party" })}
                </MyTypography>
              </TableCellHeader>
              <TableCellHeader className={classes.col180}>
                <MyTypography variant="subtitle3">
                  {intl.formatMessage({ defaultMessage: "Project Name" })}
                </MyTypography>
              </TableCellHeader>
              <TableCellHeader className={classes.col150}>
                <MyTypography variant="subtitle3">
                  {intl.formatMessage({ defaultMessage: "Company" })}
                </MyTypography>
              </TableCellHeader>
              <TableCellHeader className={classes.col120}>
                <MyTypography variant="subtitle3">
                  {intl.formatMessage({ defaultMessage: "Payment Terms" })}
                </MyTypography>
              </TableCellHeader>
              <TableCellHeader
                className={classes.col120}
                direction={
                  sort.sort === ExporOrderListSortField.createDate
                    ? getArrowDirection(sort.asc)
                    : undefined
                }
                arrowPosition="right"
                onClick={() => onSort(ExporOrderListSortField.createDate)}
              >
                <MyTypography variant="subtitle3">
                  {intl.formatMessage({ defaultMessage: "Create Date" })}
                </MyTypography>
              </TableCellHeader>
              <TableCellHeader className={classes.col120}>
                <MyTypography variant="subtitle3">
                  {intl.formatMessage({ defaultMessage: "Order Status" })}
                </MyTypography>
                <MyTypography variant="body3">
                  {intl.formatMessage({ defaultMessage: "(eOrdering)" })}
                </MyTypography>
              </TableCellHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {renderCollection(data, (item, index) => (
              <TableRow className={classes.rowClickable} key={index}>
                <TableCell
                  className={classNames(
                    classes.colViewDuplicate,
                    classes.col96
                  )}
                >
                  <Box justifyContent={"center"} className={classes.flexStart}>
                    <Button
                      className={classes.printBtn}
                      variant="outlined"
                      onClick={e => {
                        e.preventDefault();
                        handleDownloadPdf(item);
                      }}
                      color="secondary"
                    >
                      <MyTypography variant="body2">
                        {intl.formatMessage({
                          defaultMessage: "Print"
                        })}
                      </MyTypography>
                    </Button>
                  </Box>
                </TableCell>
                <TableCell
                  className={classNames(classes.colPiNumber, classes.col100)}
                >
                  <MyTypography variant="subtitle3">
                    {item ? (
                      <MyTypography variant="subtitle3">
                        {item.sdDoc}
                      </MyTypography>
                    ) : (
                      <Skeleton />
                    )}
                  </MyTypography>
                </TableCell>
                <TableCell
                  className={classNames(classes.col120, classes.firstChild)}
                >
                  {item ? <Box pl={1}>{item.poNo}</Box> : <Skeleton />}
                </TableCell>
                <TableCell className={classNames(classes.col120)}>
                  {item ? (
                    <MyTypography variant="body3">
                      {item.contractPi}
                    </MyTypography>
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell className={classes.col180}>
                  {`${item.soldTo || ""}-${item.soldToName1 || ""}`}
                </TableCell>
                <TableCell className={classes.col180}>
                  {item ? (
                    <MyTypography variant="body3">
                      {item?.descriptionInContract}
                    </MyTypography>
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell
                  style={{
                    whiteSpace: "nowrap"
                  }}
                  className={classes.col150}
                >
                  {item ? (
                    <MyTypography variant="body3">{item.salesOrg}</MyTypography>
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell className={classes.col150}>
                  {item ? (
                    <MyTypography variant="body3">
                      {item.paymentTerm} - {item.paymentTermDesc}
                    </MyTypography>
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell className={classes.col120}>
                  {item ? (
                    <MyTypography variant="body3">
                      {item.createDate}
                    </MyTypography>
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell className={classes.col180}>
                  {item ? (
                    <MyTypography variant="body3">
                      {renderStatus(item.eOrderingStatus)}
                    </MyTypography>
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {data?.length ? (
          <StickyHorizontalScrollBar container={containerRef} />
        ) : null}
      </div>
      <Box className={classes.paginateFooter}>
        <TablePaginationCustom
          settings={settings}
          hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
          onNextPage={onNextPage}
          onPreviousFirstPage={onPreviousFirstPage}
          onNextLastPage={onNextLastPage}
          onUpdateListSettings={onUpdateListSettings}
          hasPreviousPage={
            pageInfo && !disabled ? pageInfo.hasPreviousPage : false
          }
          onPreviousPage={onPreviousPage}
          latestPageItemNumber={latestPageItemNumber}
          totalCount={totalCount || 0}
          disabled={disabled}
          isBackFistPage
          isNextLastPage
          rowNumberSet={BIG_ROW_NUMBER_SET}
        />
      </Box>
    </>
  );
};

export default OrderListTable;
